import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { subscriptionAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { AlertTriangle, CreditCard } from 'lucide-react';
import { logger } from '../lib/logger';

/**
 * SubscriptionGuard - Blocks access to app features when subscription is expired
 * Allows access only to:
 * - /subscription (to renew)
 * - /settings/* (to manage account)
 * - /logout
 */
// Paths that are allowed even when subscription is expired
const allowedPaths = ['/subscription', '/settings/profile', '/settings/institution', '/pricing'];

export default function SubscriptionGuard({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const hasInstitution = !!user?.institution_id;
  const [checking, setChecking] = useState(true);
  const [isExpired, setIsExpired] = useState(false);
  
  const checkSubscriptionStatus = useCallback(async () => {
    if (!hasInstitution) {
      // Platform admins have no institution subscription to check
      setIsExpired(false);
      setChecking(false);
      return;
    }
    try {
      const response = await subscriptionAPI.getCurrent();
      const subscription = response.data;
      
      // Check if subscription is expired or trial expired
      const status = subscription?.subscription_status || subscription?.status;
      
      if (status === 'expired' || status === 'trial_expired') {
        setIsExpired(true);
        
        // If trying to access a restricted page, redirect to subscription
        if (!allowedPaths.some(path => location.pathname.startsWith(path))) {
          navigate('/subscription');
        }
      } else {
        setIsExpired(false);
      }
    } catch (error) {
      logger.error('Subscription check failed:', error);
      // If check fails, allow access (fail open)
      setIsExpired(false);
    } finally {
      setChecking(false);
    }
  }, [location.pathname, navigate, hasInstitution]);

  useEffect(() => {
    checkSubscriptionStatus();
  }, [location.pathname, checkSubscriptionStatus]);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-500">Checking subscription status...</div>
      </div>
    );
  }
  
  // If expired and trying to access restricted page, show subscription prompt
  if (isExpired && !allowedPaths.some(path => location.pathname.startsWith(path))) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-lg">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <CardTitle className="text-2xl">Subscription Expired</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-slate-600">
              Your trial period has ended. Please subscribe to continue using HMC Buddy.
            </p>
            <Button 
              onClick={() => navigate('/subscription')}
              className="bg-red-500 hover:bg-red-600 w-full"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              View Subscription Plans
            </Button>
            <p className="text-sm text-slate-500">
              Choose from our flexible plans starting at ₹999/month
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return children;
}
